import { type FC, useMemo } from 'react'
import classNames from 'classnames'
import { SelectDropdown, type SelectOption } from '../../../components/SelectDropdown/SelectDropdown'
import { dropdownMessage, dropdownSectionHeader } from '../../../components/SelectDropdown/dropdownOptions'
import { useContactSearch } from '../../../common/useContactSearch'
import { contactOptionOf } from '../domain/contactFallback'
import { WARNING_BORDER } from '../constants'
import { type ContactOption, type WizardHolder } from '../domain/types'

const _MANUAL_HOLDER = '__manual__'

const _MANUAL_OPTION: SelectOption = { id: _MANUAL_HOLDER, label: 'Someone else…' }

const _NO_MATCH_MESSAGE = 'No matching contacts. Choose “Someone else…” to type a name.'

interface HolderPersonPickerProps {
  accountId: string
  holder: WizardHolder
  disabled: boolean
  suggested: ContactOption[]
  onPick: (contact: ContactOption | null) => void
  onTypeByHand: () => void
}

export const HolderPersonPicker: FC<HolderPersonPickerProps> = ({ accountId, holder, disabled, suggested, onPick, onTypeByHand }) => {
  const { data: searchResults, search, setSearch, isFetching } = useContactSearch({ accountId })

  const found = useMemo(
    () => searchResults.map(contactOptionOf).filter((option) => !suggested.some((candidate) => candidate.id === option.id)),
    [searchResults, suggested],
  )

  const options = useMemo(() => {
    const sections: SelectOption[] = []
    if (suggested.length > 0) {
      sections.push(dropdownSectionHeader('suggested', 'From this meeting'), ...suggested.map((option) => ({ id: option.id, label: option.name })))
    }
    if (found.length > 0) {
      sections.push(dropdownSectionHeader('contacts', 'Contacts'), ...found.map((option) => ({ id: option.id, label: option.name })))
    }
    if (sections.length === 0 && (search ?? '').trim().length > 0 && !isFetching) {
      sections.push(dropdownMessage(_NO_MATCH_MESSAGE))
    }
    const linkedElsewhere =
      holder.nameSource === 'contact' &&
      holder.contactId &&
      !suggested.some((option) => option.id === holder.contactId) &&
      !found.some((option) => option.id === holder.contactId)
    if (linkedElsewhere && holder.contactId) {
      sections.push({ id: holder.contactId, label: holder.name })
    }
    return [...sections, _MANUAL_OPTION]
  }, [suggested, found, search, isFetching, holder.nameSource, holder.contactId, holder.name])

  const selected = useMemo(() => {
    if (holder.nameSource === 'manual') {
      return _MANUAL_OPTION
    }
    return holder.nameSource === 'contact' && holder.contactId ? { id: holder.contactId, label: holder.name } : null
  }, [holder.nameSource, holder.contactId, holder.name])

  const handleSelected = (option: SelectOption) => {
    if (option.id === _MANUAL_HOLDER) {
      onTypeByHand()
      return
    }
    onPick([...suggested, ...found].find((candidate) => candidate.id === option.id) ?? null)
  }

  return (
    <SelectDropdown
      options={options}
      initialSelectedOption={selected}
      placeholder='Search contacts…'
      searchable
      loading={isFetching}
      inputAutoComplete='off'
      disabled={disabled}
      className={classNames(holder.nameSource === null ? [WARNING_BORDER, 'rounded-2'] : null)}
      onSearchInputChange={async (term) => setSearch(term)}
      onOptionSelected={handleSelected}
    />
  )
}
